import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allDevicesStore } from "../../stores/devices/all_devices_store"
import { devicesLoadingStore } from "../../stores/devices/devices_loading_store"
import { filteredItemsStore } from "../../stores/items/filtered_items_store"
import { regionStore } from "../../stores/locales/region_store"
import { buildingStore } from "../../stores/locales/building_store"
import { areaStore } from "../../stores/locales/area_store"
import { roomStore } from "../../stores/locales/room_store"
import { sendFailureNotification } from "shared/toast_notifications"
import Toastify from "toastify-js"

/**
 * Item Filters Controller
 *
 * Manages client-side filtering of devices based on user-selected filter criteria.
 * Reads filter values from form selects OR area store, applies filtering logic to
 * devices from allDevicesStore, and populates filteredItemsStore with the results.
 *
 * Supports two usage modes:
 * 1. With filter UI (Manage Devices page): User selects filters and clicks Apply
 * 2. Without filter UI (Edit Areas page): Area selection automatically filters devices
 *
 * Connects to data-controller="filters--item-filters"
 */
export default class extends Controller {

    static stores = [
        allDevicesStore,
        devicesLoadingStore,
        filteredItemsStore,
        regionStore,
        buildingStore,
        areaStore,
        roomStore
    ]

    static targets = [
        "regionSelect",
        "buildingSelect",
        "areaSelect",
        "roomSelect",
        "deviceTypeSelect",
        "manufacturerSelect",
        "modelSelect",
        "deploymentStatusSelect",
        "bundleTypeSelect",
        "projectSelect",
        "clearFiltersButton",
        "table"
    ]

    static values = {
        itemTypeLabel: { type: String, default: "devices" }
    }

    /**
     * Initialize controller and subscribe to stores
     */
    connect() {
        useStore(this)
    }

    /**
     * React to changes in the all devices store
     *
     * This callback is triggered whenever allDevicesStore is updated, typically when:
     * - Initial device load completes
     * - User changes the selected area
     * - Area is cleared (devices set to empty array)
     *
     * Behavior:
     * - If allDevicesStore is cleared (empty array), clear filteredItemsStore as well
     * - If filters are currently active, re-apply them to the new device list
     * - If no filters are active, do nothing (wait for user to click Apply)
     *
     * This ensures that when a user changes areas while filters are applied,
     * the filtered view updates automatically to show filtered devices from the new area.
     *
     * @param {Array} devices - The new device list from allDevicesStore
     */
    onAllDevicesStoreUpdate(devices) {
        // Handle case where allDevicesStore is cleared (area cleared or error occurred)
        // When devices is an empty array, check if we're still loading
        if (Array.isArray(devices) && devices.length === 0) {
            // If still loading, do nothing - wait for devices to arrive
            if (this.devicesLoadingStoreValue === true) {
                return
            }

            // Not loading and empty means either "not loaded yet" or "loaded but empty"
            // Clear filtered items store if it has items
            if (this.filteredItemsStoreValue && this.filteredItemsStoreValue.length > 0) {
                this.setFilteredItemsStoreValue([])
            }
            return
        }

        // Check if filters are currently active
        const filtersAreActive = this.areFiltersActive()

        // If filters are active, re-apply them to the new device list
        if (filtersAreActive) {
            try {
                // Read current filter criteria from form selects
                const criteria = this.getFilterCriteria()

                // Apply filtering logic to new device list
                const filteredDevices = this.filterDevices(criteria)

                // Update filteredItemsStore with newly filtered results
                this.setFilteredItemsStoreValue(filteredDevices)
            } catch (error) {
                // Log error but don't disrupt user experience
                console.error('Error re-applying filters after store update:', {
                    error: error.message,
                    stack: error.stack,
                    timestamp: new Date().toISOString()
                })

                // On error, fall back to showing all devices
                this.setFilteredItemsStoreValue(devices || [])
            }
        }

        // If no filters are active, do nothing
        // Wait for user to explicitly click Apply button
        // This prevents the table from auto-populating before user is ready
    }

    /**
     * Check if filters are currently active
     *
     * Determines whether filters are active by checking all filter criteria from stores.
     *
     * This is more reliable than checking if filteredItemsStore has been populated,
     * because filteredItemsStore could be empty due to no matching results rather
     * than no filters being applied.
     *
     * @returns {boolean} True if any filter is active, false otherwise
     */
    areFiltersActive() {
        // Get filter criteria from stores and select targets
        const criteria = this.getFilterCriteria()

        return criteria.regionId !== null ||
            criteria.buildingId !== null ||
            criteria.areaId !== null ||
            criteria.roomId !== null ||
            criteria.deviceTypeId !== null ||
            criteria.manufacturerId !== null ||
            criteria.modelId !== null ||
            criteria.deploymentStatusId !== null ||
            criteria.bundleTypeId !== null ||
            (criteria.projectIds !== null && criteria.projectIds.length > 0)
    }

    /**
     * Clear all filters and display all devices
     *
     * Triggered by the Clear Filters button click event.
     * Resets all TomSelect filter instances to empty state and updates
     * filteredItemsStore with all devices from allDevicesStore.
     *
     * Handles the following scenarios:
     * - Organization has devices: Shows all devices in table
     * - Organization has no devices: Shows empty table with "No matching records found"
     * - Devices still loading: Shows empty array (will update when devices load)
     *
     * No server requests are made during this operation - it's purely client-side.
     *
     * @param {Event} event - Click event from Clear Filters button
     */
    clearFilters(event) {
        event.preventDefault()

        try {
            // Clear TomSelect instances for each filter target that exists on this page
            if (this.hasRegionSelectTarget && this.regionSelectTarget.tomselect) {
                this.regionSelectTarget.tomselect.clear()
            }
            if (this.hasBuildingSelectTarget && this.buildingSelectTarget.tomselect) {
                this.buildingSelectTarget.tomselect.clear()
            }
            if (this.hasAreaSelectTarget && this.areaSelectTarget.tomselect) {
                this.areaSelectTarget.tomselect.clear()
            }
            if (this.hasRoomSelectTarget && this.roomSelectTarget.tomselect) {
                this.roomSelectTarget.tomselect.clear()
            }
            if (this.hasDeviceTypeSelectTarget && this.deviceTypeSelectTarget.tomselect) {
                this.deviceTypeSelectTarget.tomselect.clear()
            }
            if (this.hasManufacturerSelectTarget && this.manufacturerSelectTarget.tomselect) {
                this.manufacturerSelectTarget.tomselect.clear()
            }
            if (this.hasModelSelectTarget && this.modelSelectTarget.tomselect) {
                this.modelSelectTarget.tomselect.clear()
            }
            if (this.hasDeploymentStatusSelectTarget && this.deploymentStatusSelectTarget.tomselect) {
                this.deploymentStatusSelectTarget.tomselect.clear()
            }
            if (this.hasBundleTypeSelectTarget && this.bundleTypeSelectTarget.tomselect) {
                this.bundleTypeSelectTarget.tomselect.clear()
            }
            if (this.hasProjectSelectTarget && this.projectSelectTarget.tomselect) {
                this.projectSelectTarget.tomselect.clear()
            }

            // Clear locale stores so areFiltersActive() returns false after clear
            this.setRegionStoreValue({})
            this.setBuildingStoreValue({})
            this.setAreaStoreValue({})
            this.setRoomStoreValue({})

            // Update filteredItemsStore with all devices from allDevicesStore
            const allDevices = this.allDevicesStoreValue || []
            this.setFilteredItemsStoreValue(allDevices)

            // Disable button based on whether any filters remain active
            if (this.hasClearFiltersButtonTarget) {
                this.clearFiltersButtonTarget.disabled = !this.areFiltersActive()
            }

        } catch (error) {
            // Log error with context for debugging
            console.error('Error clearing filters:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            })

            // Display user-friendly error notification
            sendFailureNotification('Unable to clear filters. Please try again.')
        }
    }

    /**
     * Apply filters to devices when user clicks Apply button
     *
     * Triggered by the Apply Filters button click event.
     * Reads filter criteria from form selects, waits for devices to load if necessary,
     * applies filtering logic, and updates the filteredItemsStore with results.
     *
     * Handles the following scenarios:
     * - Devices still loading: Shows progress toast, waits for load, then filters
     * - No devices: Filters empty array, resulting in empty filteredItemsStore
     * - Devices loaded: Immediately applies filters
     *
     * @param {Event} event - Click event from Apply button
     */
    async applyFilters(event) {
        event.preventDefault()

        // Prevent spam-clicking while already fetching
        if (this._isApplying) {
            return
        }

        this._isApplying = true
        this._disableApplyButton()

        try {
            // Check if devices are still loading using the loading store
            if (this.devicesLoadingStoreValue === true) {
                // Show persistent progress toast
                this._showProgressToast()

                // Wait for devices to finish loading
                await this.waitForDevices()

                // Dismiss progress toast
                this._dismissProgressToast()
            }

            // Read filter criteria from form selects
            const criteria = this.getFilterCriteria()

            // Apply filtering logic to devices
            const filteredDevices = this.filterDevices(criteria)

            // Update filteredItemsStore with results
            this.setFilteredItemsStoreValue(filteredDevices)

            // Enable Clear Filters button only if filters were applied
            if (this.hasClearFiltersButtonTarget) {
                this.clearFiltersButtonTarget.disabled = !this.areFiltersActive()
            }

        } catch (error) {
            // Dismiss progress toast on error
            this._dismissProgressToast()

            // Log error with context for debugging
            console.error('Error applying filters:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            })

            // Display user-friendly error notification
            sendFailureNotification('Unable to apply filters. Please try again.')
        } finally {
            this._isApplying = false
            this._enableApplyButton()
        }
    }

    /**
     * React to changes in filteredItemsStore — update progress toast with count
     */
    onFilteredItemsStoreUpdate(items) {
        if (this._progressToast && this._isApplying && Array.isArray(items)) {
            this._updateProgressToast(`Fetching ${this.itemTypeLabelValue}... (${items.length} fetched)`)
        }
    }

    /**
     * React to changes in devicesLoadingStore — dismiss toast when loading completes
     */
    onDevicesLoadingStoreUpdate(loading) {
        if (loading === false && this._progressToast) {
            this._dismissProgressToast()
        }
    }

    /**
     * Wait for devices to finish loading from the server
     *
     * Polls the devicesLoadingStore until it transitions from true (loading)
     * to false (loaded).
     *
     * Uses a 100ms polling interval with a 30-second timeout to prevent
     * infinite waiting if the fetch fails or hangs.
     *
     * @returns {Promise<void>} Resolves when devices are loaded or timeout occurs
     */
    async waitForDevices() {
        return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
                // Wait until loading is false
                if (this.devicesLoadingStoreValue === false) {
                    clearInterval(checkInterval)
                    resolve()
                }
            }, 100)

            // Timeout after 30 seconds to prevent infinite waiting
            setTimeout(() => {
                clearInterval(checkInterval)
                resolve()
            }, 30000)
        })
    }

    /**
     * Extract filter criteria from stores and select targets
     *
     * Location filters (region, building, area, room) are read from their respective stores,
     * which are populated by the select controllers. This provides a single source of truth.
     *
     * Device type filters are read from select targets when available.
     *
     * Empty or invalid values are converted to null to be ignored during filtering.
     *
     * @returns {Object} FilterCriteria object with properties:
     *   - regionId: string | null
     *   - buildingId: string | null
     *   - areaId: string | null
     *   - roomId: string | null
     *   - deviceTypeId: string | null
     *   - manufacturerId: string | null
     *   - modelId: string | null
     *   - deploymentStatusId: string | null
     */
    getFilterCriteria() {
        const criteria = {
            // Location filters from stores (populated by select controllers)
            regionId: this.regionStoreValue?.id || null,
            buildingId: this.buildingStoreValue?.id || null,
            areaId: this.areaStoreValue?.id || null,
            roomId: this.roomStoreValue?.id || null,

            // Device type filters from select targets (if available)
            deviceTypeId: this.hasDeviceTypeSelectTarget ? this.getSelectValue(this.deviceTypeSelectTarget) : null,
            manufacturerId: this.hasManufacturerSelectTarget ? this.getSelectValue(this.manufacturerSelectTarget) : null,
            modelId: this.hasModelSelectTarget ? this.getSelectValue(this.modelSelectTarget) : null,
            deploymentStatusId: this.hasDeploymentStatusSelectTarget ? this.getSelectValue(this.deploymentStatusSelectTarget) : null,

            // Bundle type filter (for bundle instances)
            bundleTypeId: this.hasBundleTypeSelectTarget ? this.getSelectValue(this.bundleTypeSelectTarget) : null,

            // Project filter (multi-select returns array of project IDs)
            projectIds: this.hasProjectSelectTarget ? this._getMultiSelectValues(this.projectSelectTarget) : null
        }

        return criteria
    }

    /**
     * Legacy method kept for backward compatibility
     *
     * @deprecated Use getFilterCriteria() instead
     * @returns {Object} FilterCriteria object
     */
    _legacyGetFilterCriteria() {
        // Mode 1: With filter UI (Manage Devices page)
        // Check if we have filter select targets
        if (this.hasRegionSelectTarget) {
            return {
                regionId: this.getSelectValue(this.regionSelectTarget),
                buildingId: this.getSelectValue(this.buildingSelectTarget),
                areaId: this.getSelectValue(this.areaSelectTarget),
                roomId: this.getSelectValue(this.roomSelectTarget),
                deviceTypeId: this.getSelectValue(this.deviceTypeSelectTarget),
                manufacturerId: this.getSelectValue(this.manufacturerSelectTarget),
                modelId: this.getSelectValue(this.modelSelectTarget),
                deploymentStatusId: this.getSelectValue(this.deploymentStatusSelectTarget),
                bundleTypeId: this.hasBundleTypeSelectTarget ? this.getSelectValue(this.bundleTypeSelectTarget) : null
            }
        }

        // Mode 2: Without filter UI (Edit Areas page)
        // Use areaStore value if available
        if (this.areaStoreValue && this.areaStoreValue.id) {
            return {
                regionId: this.areaStoreValue.region || null,
                buildingId: this.areaStoreValue.building || null,
                areaId: this.areaStoreValue.id,
                roomId: null,
                deviceTypeId: null,
                manufacturerId: null,
                modelId: null,
                deploymentStatusId: null,
                bundleTypeId: null
            }
        }

        // No filters active
        return {
            regionId: null,
            buildingId: null,
            areaId: null,
            roomId: null,
            deviceTypeId: null,
            manufacturerId: null,
            modelId: null,
            deploymentStatusId: null,
            bundleTypeId: null
        }
    }

    /**
     * Safely extract value from a TomSelect element
     *
     * Handles edge cases:
     * - Missing/undefined select element
     * - Select element without TomSelect instance
     * - Empty string values
     * - Invalid values
     *
     * @param {HTMLElement} selectElement - The select element with TomSelect instance
     * @returns {string | null} The selected value or null if empty/invalid
     */
    getSelectValue(selectElement) {
        // Handle missing element
        if (!selectElement) {
            return null
        }

        // Handle missing TomSelect instance
        if (!selectElement.tomselect) {
            return null
        }

        // Get value from TomSelect
        const value = selectElement.tomselect.getValue()

        // Convert empty strings and invalid values to null
        if (value === '' || value === undefined || value === null) {
            return null
        }

        // Return the value as a string
        return String(value)
    }

    /**
     * Extract multiple selected values from a TomSelect multi-select element
     *
     * @param {HTMLElement} selectElement - The select element with TomSelect instance
     * @returns {Array|null} Array of selected values, or null if none selected
     */
    _getMultiSelectValues(selectElement) {
        if (!selectElement || !selectElement.tomselect) {
            return null
        }

        const value = selectElement.tomselect.getValue()

        // TomSelect returns a comma-separated string for multi-select or an array
        if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
            return null
        }

        if (Array.isArray(value)) {
            return value.filter(v => v && v !== '')
        }

        // Comma-separated string
        return value.split(',').filter(v => v && v !== '')
    }

    /**
     * Filter devices based on provided criteria using AND logic
     *
     * Applies multiple filter criteria to the devices from allDevicesStore.
     * All non-null criteria must match for a device to be included in results.
     * Handles nested property access safely to avoid errors with missing data.
     *
     * Supported filters:
     * - regionId: Filters by device.locale.region.id
     * - buildingId: Filters by device.locale.building.id
     * - areaId: Filters by device.locale.area.id
     * - roomId: Filters by device.locale.room.id
     * - deviceTypeId: Filters by device.device.device_type.id
     * - manufacturerId: Filters by device.device.manufacturer.id
     * - modelId: Filters by device.device.model.id
     * - deploymentStatusId: Filters by device.deployment_status.id
     *
     * @param {Object} criteria - FilterCriteria object with filter values
     * @returns {Array} Filtered array of devices matching all criteria
     */
    filterDevices(criteria) {
        // Get devices from store, default to empty array if null/undefined
        const devices = this.allDevicesStoreValue || []

        // Filter devices using AND logic - device must match ALL non-null criteria
        return devices.filter(device => {
            // Region filter
            if (criteria.regionId && device.locale?.region?.id !== criteria.regionId) {
                return false
            }

            // Building filter
            if (criteria.buildingId && device.locale?.building?.id !== criteria.buildingId) {
                return false
            }

            // Area filter
            if (criteria.areaId && device.locale?.area?.id !== criteria.areaId) {
                return false
            }

            // Room filter
            if (criteria.roomId && device.locale?.room?.id !== criteria.roomId) {
                return false
            }

            // Device type filter
            if (criteria.deviceTypeId && device.device?.device_type?.id !== criteria.deviceTypeId) {
                return false
            }

            // Manufacturer filter
            if (criteria.manufacturerId && device.device?.manufacturer?.id !== criteria.manufacturerId) {
                return false
            }

            // Model filter
            if (criteria.modelId && device.device?.model?.id !== criteria.modelId) {
                return false
            }

            // Deployment status filter
            if (criteria.deploymentStatusId && device.deployment_status?.id !== criteria.deploymentStatusId) {
                return false
            }

            // Bundle type filter (for bundle instances)
            // This filters by the bundle definition ID (bundle_id field)
            if (criteria.bundleTypeId && device.bundle && (device.bundle.id !== criteria.bundleTypeId)) {
                return false
            }

            // Project filter (multi-select — match any of the selected project IDs)
            if (criteria.projectIds && criteria.projectIds.length > 0) {
                const deviceProjectId = device.project_id || device.project?.id
                if (!deviceProjectId || !criteria.projectIds.includes(deviceProjectId)) {
                    return false
                }
            }

            // Device matches all criteria
            return true
        })
    }

    /**
     * Show loading spinner while waiting for devices to load
     *
     * Uses bootstrap-table's built-in loading overlay for all tables on the page.
     * Also shows the report spinner if it exists (Reports page).
     *
     * @param {string} message - Message to display in spinner
     * @private
     */
    _showSpinner(message = 'Loading...') {
        // Show bootstrap-table loading overlay for all tables
        this.showLoadingSpinner()

        // Try to find report spinner (Reports page) and show it with a custom message
        const reportSpinner = document.getElementById('report_spinner')
        if (reportSpinner) {
            const messageElement = reportSpinner.querySelector('.h3')
            if (messageElement) {
                messageElement.textContent = message
            }
            reportSpinner.style.display = 'block'
        }
    }

    /**
     * Hide loading spinner after devices are loaded
     *
     * Hides bootstrap-table loading overlay and report spinner if it exists.
     *
     * @private
     */
    _hideSpinner() {
        // Hide bootstrap-table loading overlay for all tables
        this.hideLoadingSpinner()

        const reportSpinner = document.getElementById('report_spinner')
        if (reportSpinner) {
            reportSpinner.style.display = 'none'
        }
    }

    /**
     * Show loading spinner on the table element
     *
     * Routes the loading signal through the shared argus--table overlay
     * (the single source of truth for the overlay lifecycle and aria-busy),
     * rather than manipulating any inline loading UI directly.
     */
    showLoadingSpinner() {
        const argusTable = this._getArgusTable()
        if (!argusTable) {
            console.warn('filters--item-filters: No argus--table controller found')
            return
        }

        argusTable.showLoading()
    }

    /**
     * Hide loading spinner on the table element
     *
     * Routes the hide signal through the shared argus--table overlay.
     */
    hideLoadingSpinner() {
        const argusTable = this._getArgusTable()
        if (!argusTable) {
            return
        }

        argusTable.hideLoading()
    }

    /**
     * Get the argus--table controller instance from the table target element.
     *
     * Follows the established convention used across table controllers
     * (see table--items-table) for reaching the sibling argus--table.
     *
     * @returns {Object|null} The argus--table controller instance, or null if unavailable
     */
    _getArgusTable() {
        if (!this.hasTableTarget) {
            return null
        }

        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    // ─── Progress Toast Helpers ─────────────────────────────────────

    /**
     * Show a persistent progress toast in the bottom-right with a spinner
     * @private
     */
    _showProgressToast() {
        const message = `Fetching ${this.itemTypeLabelValue}...`
        this._progressToast = Toastify({
            text: message,
            duration: -1,
            close: false,
            gravity: "bottom",
            position: "right",
            stopOnFocus: true,
            style: {
                background: "#6c757d",
                minWidth: "220px",
            }
        })
        this._progressToast.showToast()
    }

    /**
     * Update the progress toast message with current count
     * @param {string} message
     * @private
     */
    _updateProgressToast(message) {
        if (this._progressToast && this._progressToast.toastElement) {
            this._progressToast.toastElement.querySelector('.toast-text, .toastify-content, div')
            // Toastify stores text in the root element's textContent or a child
            this._progressToast.toastElement.textContent = `⏳ ${message}`
        }
    }

    /**
     * Dismiss the progress toast
     * @private
     */
    _dismissProgressToast() {
        if (this._progressToast) {
            this._progressToast.hideToast()
            this._progressToast = null
        }
    }

    /**
     * Disable the Apply Filters button to prevent spam-clicking
     * @private
     */
    _disableApplyButton() {
        const btn = this.element.querySelector('[data-action*="applyFilters"]')
        if (btn) {
            btn.disabled = true
            btn.classList.add('disabled')
        }
    }

    /**
     * Re-enable the Apply Filters button
     * @private
     */
    _enableApplyButton() {
        const btn = this.element.querySelector('[data-action*="applyFilters"]')
        if (btn) {
            btn.disabled = false
            btn.classList.remove('disabled')
        }
    }
}
